import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { useToast } from '@/hooks/use-toast';
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const {
    updatePassword,
    status,
    loading,
    isRecoveryMode,
    handleRecoveryFlow,
    clearRecoveryMode
  } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Проверяем recovery mode при загрузке
  useEffect(() => {
    handleRecoveryFlow();
  }, [handleRecoveryFlow]);

  // Проверяем, что пользователь в recovery режиме
  useEffect(() => {
    if (status === 'unauthenticated' && !isRecoveryMode) {
      toast({
        title: t('common.error'),
        description: t('auth.reset_password.error_link_invalid'),
        variant: "destructive",
      });
      navigate('/login');
    }
  }, [status, isRecoveryMode, navigate, toast, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast({
        title: t('common.error'),
        description: t('auth.reset_password.error_short_password'),
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: t('common.error'),
        description: t('auth.reset_password.error_mismatch'),
        variant: "destructive",
      });
      return;
    }

    const { error } = await updatePassword(newPassword);

    if (error) {
      toast({
        title: t('common.error'),
        description: error.message, // Уже переведено в AuthContext
        variant: "destructive",
      });
    } else {
      clearRecoveryMode(); // Очищаем recovery режим
      toast({
        title: t('common.success'),
        description: t('auth.reset_password.success_toast'),
      });
      navigate('/dashboard');
    }
  };

  // Показываем loading пока проверяем recovery mode
  if (status === 'loading' || (status === 'authenticated' && !isRecoveryMode)) {
    return (
      <MainLayout>
        <div className="container-page flex items-center justify-center py-8 md:py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-muted-foreground">{t('auth.reset_password.checking_link')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container-page flex items-center justify-center py-8 md:py-16">
        <div className="w-full max-w-md bg-card p-6 md:p-8 rounded-lg border shadow-sm">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold">{t('auth.reset_password.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('auth.reset_password.subtitle')}
            </p>
          </div>

          {/* Recovery mode indicator */}
          {isRecoveryMode && (
            <Alert className="mb-4 border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {t('auth.reset_password.recovery_confirmed')}
              </AlertDescription>
            </Alert>
          )}

          {/* Not in recovery mode alert */}
          {!isRecoveryMode && status === 'authenticated' && (
            <Alert className="mb-4 border-orange-200 bg-orange-50">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-800">
                {t('auth.reset_password.not_recovery_mode')}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">{t('auth.reset_password.new_password')}</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('auth.reset_password.new_password_placeholder')}
                required
                minLength={6}
                disabled={!isRecoveryMode}
              />
              <p className="text-xs text-muted-foreground">
                {t('auth.common.min_chars')}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('auth.reset_password.confirm_password')}</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('auth.reset_password.confirm_password_placeholder')}
                required
                disabled={!isRecoveryMode}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || !isRecoveryMode}
            >
              {loading ? t('auth.reset_password.updating_btn') : t('auth.reset_password.update_btn')}
            </Button>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

export default ResetPassword;
